import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Landing.css';

const Landing: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={`landing ${isVisible ? 'visible' : ''}`}>
      {/* Floating Background Elements */}
      <div className="landing-bg-elements">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
        <div className="grain-overlay"></div>
      </div>

      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <span className="logo-icon">💜</span>
            <span className="logo-text">MalDolor</span>
          </div>
          <div className="nav-links">
            <Link to="/login" className="nav-btn">
              <span className="nav-btn-text">Iniciar Sesión</span>
              <span className="nav-btn-icon">👤</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">✨</span>
              <span>Tu compañero de bienestar personal</span>
            </div>
            <h1 className="hero-title">
              Entiende tu cuerpo,
              <span className="title-gradient"> cuida tu mente</span>
            </h1>
            <p className="hero-subtitle">
              Registra tus dolores de cabeza, tensión arterial y episodios emocionales.
              Descubre patrones, recibe recordatorios y toma el control de tu salud.
            </p>
            <div className="hero-ctas">
              <Link to="/register" className="cta-primary">
                <span>Empezar Ahora</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
              <a href="#como-funciona" className="cta-secondary">
                <span>Ver cómo funciona</span>
              </a>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <span className="stat-number">100+</span>
                <span className="stat-label">Registros</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">10</span>
                <span className="stat-label">Usuarios</span>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <span className="stat-number">3</span>
                <span className="stat-label">Tipos de tracking</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="phone-mockup">
              <div className="phone-screen">
                <div className="screen-header">
                  <span className="screen-time">9:41</span>
                  <span className="screen-title">Dashboard</span>
                </div>
                <div className="screen-card card-headache">
                  <span className="card-emoji">🤕</span>
                  <div className="card-info">
                    <span className="card-label">Dolores este mes</span>
                    <span className="card-value">3</span>
                  </div>
                </div>
                <div className="screen-card card-tension">
                  <span className="card-emoji">💓</span>
                  <div className="card-info">
                    <span className="card-label">Tensión promedio</span>
                    <span className="card-value">120/80</span>
                  </div>
                </div>
                <div className="screen-card card-mood">
                  <span className="card-emoji">😊</span>
                  <div className="card-info">
                    <span className="card-label">Estado de ánimo</span>
                    <span className="card-value">Estable</span>
                  </div>
                </div>
                <div className="screen-graph">
                  <div className="graph-bars">
                    <div className="bar" style={{height: '40%'}}></div>
                    <div className="bar" style={{height: '60%'}}></div>
                    <div className="bar" style={{height: '30%'}}></div>
                    <div className="bar" style={{height: '80%'}}></div>
                    <div className="bar" style={{height: '50%'}}></div>
                    <div className="bar" style={{height: '70%'}}></div>
                    <div className="bar active" style={{height: '45%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="floating-elements">
              <div className="float-card float-1">
                <span>🎯</span>
                <span>Logro desbloqueado!</span>
              </div>
              <div className="float-card float-2">
                <span>⏰</span>
                <span>Recordatorio: Medir tensión</span>
              </div>
              <div className="float-card float-3">
                <span>📊</span>
                <span>-20% dolores este mes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="features-container">
          <div className="section-header">
            <span className="section-tag">Funcionalidades</span>
            <h2 className="section-title">Todo lo que necesitas para entenderte mejor</h2>
            <p className="section-subtitle">
              Herramientas diseñadas para ayudarte a identificar patrones y mejorar tu bienestar
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card feature-headache">
              <div className="feature-icon">
                <svg viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" fill="url(#grad1)" opacity="0.2"/>
                  <circle cx="24" cy="24" r="12" fill="url(#grad1)"/>
                  <path d="M24 18v8M20 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <defs>
                    <linearGradient id="grad1" x1="0" y1="0" x2="48" y2="48">
                      <stop stopColor="#667eea"/>
                      <stop offset="1" stopColor="#764ba2"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <h3>Dolores de Cabeza</h3>
              <p>Registra intensidad, ubicación, duración y medicación. Identifica qué los desencadena.</p>
              <ul className="feature-list">
                <li>Mapa de ubicación del dolor</li>
                <li>Escala de intensidad visual</li>
                <li>Seguimiento de medicación</li>
              </ul>
            </div>

            <div className="feature-card feature-tension">
              <div className="feature-icon">
                <svg viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" fill="#ef4444" opacity="0.2"/>
                  <path d="M24 14c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10z" fill="#ef4444"/>
                  <path d="M24 20v8M24 20l4 4M24 20l-4 4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Tensión Arterial</h3>
              <p>Monitorea tu presión sistólica, diastólica y pulso con gráficos claros.</p>
              <ul className="feature-list">
                <li>Promedios semanales/mensuales</li>
                <li>Alertas de valores anormales</li>
                <li>Historial completo</li>
              </ul>
            </div>

            <div className="feature-card feature-mood">
              <div className="feature-icon">
                <svg viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" fill="#3b82f6" opacity="0.2"/>
                  <circle cx="24" cy="24" r="12" fill="#3b82f6"/>
                  <path d="M20 22v2M28 22v2M20 28c1 2 3 3 4 3s3-1 4-3" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Estado Emocional</h3>
              <p>Rastrea tus episodios emocionales y descubre patrones en tu bienestar mental.</p>
              <ul className="feature-list">
                <li>Registro de desencadenantes</li>
                <li>Duración e intensidad</li>
                <li>Correlación con otros síntomas</li>
              </ul>
            </div>

            <div className="feature-card feature-calendar">
              <div className="feature-icon">
                <svg viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="12" width="32" height="28" rx="4" fill="#10b981" opacity="0.2"/>
                  <rect x="12" y="16" width="24" height="20" rx="2" fill="#10b981"/>
                  <path d="M16 8v8M32 8v8" stroke="#10b981" strokeWidth="2" strokeLinecap="round"/>
                  <rect x="16" y="22" width="4" height="4" rx="1" fill="white"/>
                  <rect x="22" y="22" width="4" height="4" rx="1" fill="white" opacity="0.5"/>
                  <rect x="28" y="22" width="4" height="4" rx="1" fill="white" opacity="0.5"/>
                </svg>
              </div>
              <h3>Calendario Visual</h3>
              <p>Visualiza todos tus registros en un calendario intuitivo con código de colores.</p>
              <ul className="feature-list">
                <li>Vista mensual/semanal</li>
                <li>Código de colores por tipo</li>
                <li>Resúmenes rápidos</li>
              </ul>
            </div>

            <div className="feature-card feature-analytics">
              <div className="feature-icon">
                <svg viewBox="0 0 48 48" fill="none">
                  <rect x="8" y="8" width="32" height="32" rx="4" fill="#f59e0b" opacity="0.2"/>
                  <rect x="12" y="28" width="6" height="8" rx="1" fill="#f59e0b"/>
                  <rect x="21" y="20" width="6" height="16" rx="1" fill="#f59e0b"/>
                  <rect x="30" y="14" width="6" height="22" rx="1" fill="#f59e0b"/>
                </svg>
              </div>
              <h3>Estadísticas</h3>
              <p>Gráficos y análisis detallados para entender tus tendencias de salud.</p>
              <ul className="feature-list">
                <li>Tendencias temporales</li>
                <li>Comparativas mensuales</li>
                <li>Exportar reportes</li>
              </ul>
            </div>

            <div className="feature-card feature-reminders">
              <div className="feature-icon">
                <svg viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="20" fill="#8b5cf6" opacity="0.2"/>
                  <circle cx="24" cy="24" r="12" fill="#8b5cf6"/>
                  <path d="M24 18v7l4 4" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Recordatorios</h3>
              <p>Configura alertas personalizadas para nunca olvidar medir tu tensión o registrar síntomas.</p>
              <ul className="feature-list">
                <li>Notificaciones push</li>
                <li>Horarios personalizables</li>
                <li>Recordatorios inteligentes</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" id="como-funciona">
        <div className="hiw-container">
          <div className="section-header">
            <span className="section-tag">Cómo Funciona</span>
            <h2 className="section-title">Empieza en 3 simples pasos</h2>
          </div>

          <div className="steps-grid">
            <div className="step">
              <div className="step-number">01</div>
              <div className="step-content">
                <div className="step-icon">
                  <svg viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="20" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </div>
                <h3>Crea tu cuenta</h3>
                <p>Regístrate gratis en menos de un minuto. Sin tarjeta de crédito, sin compromisos.</p>
              </div>
            </div>

            <div className="step-connector">
              <svg viewBox="0 0 100 20" fill="none">
                <path d="M0 10h90M85 5l10 5-10 5" stroke="url(#connectorGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="connectorGrad" x1="0" y1="0" x2="100" y2="0">
                    <stop stopColor="#667eea"/>
                    <stop offset="1" stopColor="#764ba2"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div className="step">
              <div className="step-number">02</div>
              <div className="step-content">
                <div className="step-icon">
                  <svg viewBox="0 0 48 48" fill="none">
                    <rect x="10" y="8" width="28" height="32" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M16 20h16M16 28h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="34" cy="34" r="8" fill="url(#checkGrad)"/>
                    <path d="M31 34l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="checkGrad" x1="26" y1="26" x2="42" y2="42">
                        <stop stopColor="#667eea"/>
                        <stop offset="1" stopColor="#764ba2"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h3>Registra tus datos</h3>
                <p>Añade tus síntomas, tensión o estado de ánimo con formularios rápidos e intuitivos.</p>
              </div>
            </div>

            <div className="step-connector">
              <svg viewBox="0 0 100 20" fill="none">
                <path d="M0 10h90M85 5l10 5-10 5" stroke="url(#connectorGrad2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="connectorGrad2" x1="0" y1="0" x2="100" y2="0">
                    <stop stopColor="#667eea"/>
                    <stop offset="1" stopColor="#764ba2"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div className="step">
              <div className="step-number">03</div>
              <div className="step-content">
                <div className="step-icon">
                  <svg viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2"/>
                    <path d="M24 14v10l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <circle cx="24" cy="24" r="3" fill="url(#centerGrad)"/>
                    <defs>
                      <linearGradient id="centerGrad" x1="21" y1="21" x2="27" y2="27">
                        <stop stopColor="#667eea"/>
                        <stop offset="1" stopColor="#764ba2"/>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <h3>Descubre patrones</h3>
                <p>Analiza tus datos con gráficos claros y recibe insights sobre tu salud.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="testimonials-container">
          <div className="section-header">
            <span className="section-tag">Testimonios</span>
            <h2 className="section-title">Lo que dicen nuestros usuarios</h2>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <blockquote>
                "Por fin puedo mostrarle a mi médico un historial claro de mis migrañas.
                Ha cambiado completamente cómo gestionamos mi tratamiento."
              </blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">ML</div>
                <div className="author-info">
                  <span className="author-name">María L.</span>
                  <span className="author-role">Usuario desde 2024</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card featured">
              <div className="testimonial-stars">★★★★★</div>
              <blockquote>
                "La función de recordatorios me ha ayudado a ser constante con la medición de mi tensión.
                Mi cardiólogo está encantado con los datos."
              </blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">JR</div>
                <div className="author-info">
                  <span className="author-name">Juan R.</span>
                  <span className="author-role">Usuario Premium</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <blockquote>
                "Simple, bonita y útil. Exactamente lo que necesitaba para entender
                qué desencadena mis dolores de cabeza."
              </blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">AS</div>
                <div className="author-info">
                  <span className="author-name">Ana S.</span>
                  <span className="author-role">Usuario desde 2024</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta">
        <div className="cta-container">
          <div className="cta-content">
            <h2>Empieza a cuidarte hoy</h2>
            <p>Únete a cientos de usuarios que ya están tomando el control de su salud</p>
            <div className="cta-buttons">
              <Link to="/register" className="cta-primary large">
                <span>Crear Cuenta Gratis</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </Link>
            </div>
            <p className="cta-note">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              Sin tarjeta de crédito · Cancela cuando quieras
            </p>
          </div>
          <div className="cta-decoration">
            <div className="deco-circle deco-1"></div>
            <div className="deco-circle deco-2"></div>
            <div className="deco-circle deco-3"></div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="nav-logo">
                <span className="logo-icon">💜</span>
                <span className="logo-text">MalDolor</span>
              </div>
              <p>Tu compañero de bienestar personal. Entiende tu cuerpo, cuida tu mente.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Producto</h4>
                <a href="#features">Funcionalidades</a>
                <a href="#como-funciona">Cómo funciona</a>
                <Link to="/register">Registrarse</Link>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <span className="footer-link-placeholder">Privacidad</span>
                <span className="footer-link-placeholder">Términos</span>
                <span className="footer-link-placeholder">Cookies</span>
              </div>
              <div className="footer-column">
                <h4>Contacto</h4>
                <a href="mailto:info@maldolor.com">info@maldolor.com</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2024 MalDolor. Todos los derechos reservados.</p>
            <p className="made-with">Hecho con 💜 para tu bienestar</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
